/**
 * Web mock for React Native Image Crop Picker
 * Provides web-compatible image selection functionality
 */

class ImagePicker {
  static async openPicker(options = {}) {
    console.log('ImagePicker openPicker (web):', options);
    
    return new Promise((resolve, reject) => {
      // Create a file input element
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = options.mediaType === 'video' ? 'video/*' : 'image/*';
      input.multiple = options.multiple || false;
      
      input.onchange = (event) => {
        const files = Array.from(event.target.files);
        
        if (files.length === 0) {
          reject(new Error('User cancelled image selection'));
          return;
        }
        
        // Process files
        const processFile = (file) => {
          return new Promise((fileResolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              // Create a mock response similar to React Native Image Picker
              const response = {
                path: e.target.result, // Data URL
                data: e.target.result.split(',')[1], // Base64 data
                width: options.width || 300,
                height: options.height || 300,
                mime: file.type,
                size: file.size,
                filename: file.name,
                creationDate: new Date().toISOString(),
                modificationDate: new Date().toISOString(),
              };
              fileResolve(response);
            };
            reader.readAsDataURL(file);
          });
        };
        
        if (options.multiple) {
          Promise.all(files.map(processFile))
            .then(results => resolve(results))
            .catch(reject);
        } else {
          processFile(files[0])
            .then(resolve)
            .catch(reject);
        }
      };
      
      input.oncancel = () => {
        reject(new Error('User cancelled image selection'));
      };
      
      // Trigger the file picker
      input.click();
    });
  }
  
  static async openCamera(options = {}) {
    console.log('ImagePicker openCamera (web):', options);
    
    try {
      // Request camera access
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: true,
        audio: false 
      });
      
      // Create video element and canvas for capture
      return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        
        // Create modal for camera view
        const modal = document.createElement('div');
        modal.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.9);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          z-index: 10000;
        `;
        
        video.style.cssText = `
          width: 80%;
          max-width: 500px;
          border-radius: 8px;
        `;
        
        video.srcObject = stream;
        video.autoplay = true;
        video.playsInline = true;
        
        // Capture button
        const captureBtn = document.createElement('button');
        captureBtn.textContent = 'Capture Photo';
        captureBtn.style.cssText = `
          margin-top: 20px;
          padding: 12px 24px;
          font-size: 16px;
          background: #007AFF;
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
        `;
        
        // Cancel button
        const cancelBtn = document.createElement('button');
        cancelBtn.textContent = 'Cancel';
        cancelBtn.style.cssText = `
          margin-top: 10px;
          padding: 12px 24px;
          font-size: 16px;
          background: #FF3B30;
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
        `;
        
        captureBtn.onclick = () => {
          // Set canvas size to video dimensions
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          
          // Draw video frame to canvas
          context.drawImage(video, 0, 0);
          
          // Convert to data URL
          const dataURL = canvas.toDataURL('image/jpeg', 0.8);
          
          // Stop camera stream
          stream.getTracks().forEach(track => track.stop());
          document.body.removeChild(modal);
          
          // Return mock response
          resolve({
            path: dataURL,
            data: dataURL.split(',')[1],
            width: canvas.width,
            height: canvas.height,
            mime: 'image/jpeg',
            size: Math.round(dataURL.length * 0.75), // Approximate size
            filename: `camera_${Date.now()}.jpg`,
            creationDate: new Date().toISOString(),
            modificationDate: new Date().toISOString(),
          });
        };
        
        cancelBtn.onclick = () => {
          stream.getTracks().forEach(track => track.stop());
          document.body.removeChild(modal);
          reject(new Error('User cancelled camera'));
        };
        
        modal.appendChild(video);
        modal.appendChild(captureBtn);
        modal.appendChild(cancelBtn);
        document.body.appendChild(modal);
      });
    } catch (error) {
      throw new Error('Camera access denied or not available');
    }
  }
  
  static async openCropper(options = {}) {
    console.log('ImagePicker openCropper (web):', options);
    
    // For web, we'll just return the image as-is since cropping is complex
    // In a real implementation, you might integrate a web cropping library
    if (options.path) {
      return {
        path: options.path,
        data: options.path.split(',')[1],
        width: options.width || 300,
        height: options.height || 300,
        mime: 'image/jpeg',
        size: Math.round(options.path.length * 0.75),
        filename: `cropped_${Date.now()}.jpg`,
        creationDate: new Date().toISOString(),
        modificationDate: new Date().toISOString(),
      };
    }
    
    throw new Error('No image path provided for cropping');
  }
  
  static clean() {
    console.log('ImagePicker clean (web) - no-op');
    // No cleanup needed for web
    return Promise.resolve();
  }
  
  static cleanSingle(path) {
    console.log('ImagePicker cleanSingle (web):', path);
    // No cleanup needed for web
    return Promise.resolve();
  }
}

// Export the same interface as react-native-image-crop-picker
export default ImagePicker;
export const openPicker = ImagePicker.openPicker;
export const openCamera = ImagePicker.openCamera;
export const openCropper = ImagePicker.openCropper;
export const clean = ImagePicker.clean;
export const cleanSingle = ImagePicker.cleanSingle;
